import { FC, useMemo, useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CachedIcon from '@mui/icons-material/Cached';
import { SchedulingRuleDialog } from 'components/dialog/scheduling_rules/SchedulingRuleDialog';
import type { SchedulingRule } from 'repository/schedule/@types/SchedulingRuleModel';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface ContextMenuProps {
    selectedRow: SchedulingRule[];
    cleanSelected: () => void;
    rows: SchedulingRule[];
    contextMenu: { mouseX: number; mouseY: number } | null;
    setContextMenu: (value: { mouseX: number; mouseY: number } | null) => void;
    reloading: () => void;
}

const getSchedulingRuleRowId = (row: SchedulingRule) => String(row.id ?? row.ruleset_id ?? row.scheduling_rule_id ?? '');

export const ContextMenu: FC<ContextMenuProps> = ({ selectedRow, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<'add' | 'edit'>('add');

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'schedule-rules', 'schedule-rules', ['add', 'edit']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row) => getSchedulingRuleRowId(row) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selectedRow;

    const effectiveOpen = modalRoute.action === 'add' || modalRoute.action === 'edit' || open;
    const effectiveMode: 'add' | 'edit' = modalRoute.action === 'edit' ? 'edit' : mode;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('schedule-rules', 'schedule-rules');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpen(false);
    };

    const options = [
        {
            name: 'Agregar',
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('schedule-rules', 'schedule-rules', 'add'));
                setContextMenu(null);
            },
            disabled: false,
            divider: false,
            show: true,
        },
        {
            name: 'Editar',
            icon: <EditIcon />,
            click: () => {
                const selectedId = selectedRow?.[0] ? getSchedulingRuleRowId(selectedRow[0]) : '';
                if (!selectedId) return;
                navigate(buildSectionSubpageActionPath('schedule-rules', 'schedule-rules', 'edit', selectedId));
                setContextMenu(null);
            },
            disabled: selectedRow.length !== 1,
            divider: false,
            show: selectedRow.length === 1,
        },
        {
            name: 'Recargar',
            icon: <CachedIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            disabled: false,
            divider: true,
            show: true,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((opt) => opt.show)
                    .map((option, index) => (
                        <div key={index}>
                            {option.divider && <Divider />}
                            <MenuItem onClick={option.click} disabled={option.disabled}>
                                <ListItemIcon>{option.icon}</ListItemIcon>
                                <ListItemText primary={option.name} />
                            </MenuItem>
                        </div>
                    ))}
            </Menu>

            <SchedulingRuleDialog
                open={effectiveOpen}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpen(true);
                    }
                }}
                mode={effectiveMode}
                rows={rows}
                selectedRow={selectedForDialog}
                reloading={reloading}
                cleanSelected={cleanSelected}
            />
        </>
    );
};
